import { useEffect } from 'react'

/** How far a finger must travel before it counts as a swipe rather than a tap. */
const minDistance = 60

/** How much more horizontal than vertical, so a scroll is never a swipe. */
const dominance = 1.8

/**
 * Swipe left and right to move between screens.
 *
 * The natural gesture on a tablet, which is what a dashboard on a pit wall
 * often is. Only while full screen, matching the switcher — outside it the tabs
 * are on screen and a swipe would fight the page's own scrolling.
 *
 * The awkward part is that two screens genuinely want horizontal movement of
 * their own: the stint chart pans across hours, and a wide timing table can
 * scroll sideways. Stealing that would make those screens unusable on the very
 * devices this is for.
 *
 * So a swipe that begins inside something horizontally scrollable is left to
 * that thing *until it reaches the end*, and only then changes screen. That is
 * the behaviour a carousel inside a scroller has everywhere else, so it is
 * learnable rather than surprising — and it means the stint chart can still be
 * left by swiping, which an outright exclusion would have prevented.
 */
export function useSwipe(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  onSwipe: (direction: -1 | 1) => void,
) {
  useEffect(() => {
    const el = ref.current
    if (!el || !enabled) return

    let startX = 0
    let startY = 0
    let tracking = false

    const onStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      // Two fingers is a pinch or a scroll, and neither is a page turn.
      if (!touch || e.touches.length !== 1) {
        tracking = false
        return
      }
      startX = touch.clientX
      startY = touch.clientY
      tracking = true
    }

    const onEnd = (e: TouchEvent) => {
      if (!tracking) return
      tracking = false

      const touch = e.changedTouches[0]
      if (!touch) return
      const dx = touch.clientX - startX
      const dy = touch.clientY - startY

      if (Math.abs(dx) < minDistance) return
      // A diagonal drag is almost always a scroll that wandered.
      if (Math.abs(dx) < Math.abs(dy) * dominance) return

      const direction = dx < 0 ? 1 : -1
      if (blockedByScroller(e.target, el, dx)) return
      onSwipe(direction)
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchend', onEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchend', onEnd)
    }
  }, [ref, enabled, onSwipe])
}

/**
 * Whether something between the touch and the stage still has room to scroll
 * the way the finger went.
 *
 * If it has, that scroll is what the gesture meant.
 */
function blockedByScroller(target: EventTarget | null, stage: HTMLElement, dx: number): boolean {
  let node = target instanceof Node ? target : null

  while (node && node !== stage) {
    if (node instanceof HTMLElement && node.scrollWidth > node.clientWidth + 1) {
      const atStart = node.scrollLeft <= 1
      const atEnd = node.scrollLeft >= node.scrollWidth - node.clientWidth - 1
      // Swiping left moves content right, which is a scroll towards the start.
      return dx > 0 ? !atStart : !atEnd
    }
    node = node.parentNode
  }
  return false
}
